// Support Inbox - View and manage contact form & support ticket submissions
import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, Card, CardContent, Chip, IconButton, Button,
    TextField, Select, MenuItem, InputLabel, FormControl, Dialog,
    DialogTitle, DialogContent, DialogActions, Grid, Avatar, Divider,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Paper, TablePagination, Tooltip, Badge, CircularProgress, Alert
} from '@mui/material';
import {
    Inbox as InboxIcon,
    Email as EmailIcon,
    CheckCircle as CheckCircleIcon,
    AccessTime as AccessTimeIcon,
    Delete as DeleteIcon,
    Visibility as VisibilityIcon,
    Reply as ReplyIcon,
    Flag as FlagIcon,
    FilterList as FilterListIcon,
    Refresh as RefreshIcon
} from '@mui/icons-material';
import { db } from '../services/firebase';
import {
    collection, query, orderBy, onSnapshot, doc, updateDoc, deleteDoc,
    serverTimestamp, getDocs, where
} from 'firebase/firestore';

const CATEGORY_LABELS = {
    general_support: { label: 'General Support', color: '#3B82F6', icon: '💬' },
    bug_report: { label: 'Bug Report', color: '#EF4444', icon: '🐛' },
    feature_request: { label: 'Feature Request', color: '#8B5CF6', icon: '💡' },
    billing: { label: 'Billing', color: '#F59E0B', icon: '💳' },
    privacy: { label: 'Privacy & Data', color: '#6366F1', icon: '🔒' },
    press: { label: 'Press & Media', color: '#EC4899', icon: '📰' },
    partnership: { label: 'Partnership', color: '#10B981', icon: '🤝' },
    other: { label: 'Other', color: '#6B7280', icon: '📋' },
};

const STATUS_CONFIG = {
    new: { label: 'New', color: 'error' },
    open: { label: 'New', color: 'error' },
    in_progress: { label: 'In Progress', color: 'warning' },
    resolved: { label: 'Resolved', color: 'success' },
    closed: { label: 'Closed', color: 'default' },
};

const isNewStatus = (status) => status === 'new' || status === 'open';

const PRIORITY_CONFIG = {
    low: { label: 'Low', color: 'default' },
    normal: { label: 'Normal', color: 'info' },
    high: { label: 'High', color: 'warning' },
    urgent: { label: 'Urgent', color: 'error' },
};

const SupportInbox = () => {
    const [tickets, setTickets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedTicket, setSelectedTicket] = useState(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [adminNotes, setAdminNotes] = useState('');
    const [filterStatus, setFilterStatus] = useState('all');
    const [filterCategory, setFilterCategory] = useState('all');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(15);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [ticketToDelete, setTicketToDelete] = useState(null);
    const [loadError, setLoadError] = useState(null);

    // Real-time listener for support tickets
    useEffect(() => {
        let unsubscribe = () => {};
        const attach = (ordered) => {
            const q = ordered
                ? query(collection(db, 'support_tickets'), orderBy('createdAt', 'desc'))
                : query(collection(db, 'support_tickets'));
            unsubscribe = onSnapshot(q, (snapshot) => {
                const ticketData = snapshot.docs.map(docSnap => ({
                    id: docSnap.id,
                    ...docSnap.data(),
                    createdAt: docSnap.data().createdAt?.toDate?.() || new Date(),
                    updatedAt: docSnap.data().updatedAt?.toDate?.() || new Date(),
                }));
                ticketData.sort((a, b) => {
                    const ts = (value) => (value instanceof Date ? value.getTime() : 0);
                    return ts(b.createdAt) - ts(a.createdAt);
                });
                setTickets(ticketData);
                setLoadError(null);
                setLoading(false);
            }, (error) => {
                console.error('Error fetching tickets:', error);
                if (ordered) {
                    unsubscribe();
                    attach(false);
                    return;
                }
                setLoadError(error.message || 'Failed to load tickets');
                setTickets([]);
                setLoading(false);
            });
        };
        attach(true);
        return () => unsubscribe();
    }, []);

    // Filter tickets
    const filteredTickets = tickets.filter(t => {
        if (filterStatus !== 'all') {
            if (filterStatus === 'new') {
                if (!isNewStatus(t.status)) return false;
            } else if (t.status !== filterStatus) {
                return false;
            }
        }
        if (filterCategory !== 'all' && t.category !== filterCategory) return false;
        return true;
    });

    // Stats
    const stats = {
        total: tickets.length,
        new: tickets.filter(t => isNewStatus(t.status)).length,
        inProgress: tickets.filter(t => t.status === 'in_progress').length,
        resolved: tickets.filter(t => t.status === 'resolved').length,
    };

    // Open ticket detail
    const handleViewTicket = (ticket) => {
        setSelectedTicket(ticket);
        setAdminNotes(ticket.adminNotes || '');
        setDialogOpen(true);
    };

    // Update ticket status
    const handleUpdateStatus = async (ticketId, newStatus) => {
        try {
            await updateDoc(doc(db, 'support_tickets', ticketId), {
                status: newStatus,
                updatedAt: serverTimestamp(),
            });
            if (selectedTicket?.id === ticketId) {
                setSelectedTicket(prev => ({ ...prev, status: newStatus }));
            }
        } catch (error) {
            console.error('Error updating status:', error);
        }
    };

    // Update priority
    const handleUpdatePriority = async (ticketId, newPriority) => {
        try {
            await updateDoc(doc(db, 'support_tickets', ticketId), {
                priority: newPriority,
                updatedAt: serverTimestamp(),
            });
            if (selectedTicket?.id === ticketId) {
                setSelectedTicket(prev => ({ ...prev, priority: newPriority }));
            }
        } catch (error) {
            console.error('Error updating priority:', error);
        }
    };

    // Save admin notes
    const handleSaveNotes = async () => {
        if (!selectedTicket) return;
        try {
            await updateDoc(doc(db, 'support_tickets', selectedTicket.id), {
                adminNotes: adminNotes,
                updatedAt: serverTimestamp(),
            });
            setSelectedTicket(prev => ({ ...prev, adminNotes }));
        } catch (error) {
            console.error('Error saving notes:', error);
        }
    };

    // Delete ticket
    const handleDeleteTicket = async () => {
        if (!ticketToDelete) return;
        try {
            await deleteDoc(doc(db, 'support_tickets', ticketToDelete));
            setDeleteConfirmOpen(false);
            setTicketToDelete(null);
            if (selectedTicket?.id === ticketToDelete) {
                setDialogOpen(false);
                setSelectedTicket(null);
            }
        } catch (error) {
            console.error('Error deleting ticket:', error);
        }
    };

    // Format date
    const formatDate = (date) => {
        if (!date) return 'N/A';
        const d = new Date(date);
        return d.toLocaleDateString('en-US', {
            month: 'short', day: 'numeric', year: 'numeric',
            hour: '2-digit', minute: '2-digit'
        });
    };

    // Time ago
    const timeAgo = (date) => {
        if (!date) return '';
        const seconds = Math.floor((new Date() - new Date(date)) / 1000);
        if (seconds < 60) return 'just now';
        if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
        if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
        return `${Math.floor(seconds / 86400)}d ago`;
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
                <CircularProgress sx={{ color: '#FF6B9D' }} />
            </Box>
        );
    }

    return (
        <Box>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box>
                    <Typography variant="h4" fontWeight="bold">
                        📬 Support Inbox
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Manage contact form submissions and support tickets
                    </Typography>
                </Box>
            </Box>

            {loadError && (
                <Alert severity="error" sx={{ mb: 2 }}>{loadError}</Alert>
            )}

            {/* Stats Cards */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid size={{ xs: 6, md: 3 }}>
                    <Card sx={{ bgcolor: '#FFF5F5', border: '1px solid #FED7D7' }}>
                        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                            <Typography variant="h3" fontWeight="bold" color="#E53E3E">{stats.new}</Typography>
                            <Typography variant="body2" color="text.secondary">New Messages</Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid size={{ xs: 6, md: 3 }}>
                    <Card sx={{ bgcolor: '#FFFAF0', border: '1px solid #FEEBC8' }}>
                        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                            <Typography variant="h3" fontWeight="bold" color="#D69E2E">{stats.inProgress}</Typography>
                            <Typography variant="body2" color="text.secondary">In Progress</Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid size={{ xs: 6, md: 3 }}>
                    <Card sx={{ bgcolor: '#F0FFF4', border: '1px solid #C6F6D5' }}>
                        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                            <Typography variant="h3" fontWeight="bold" color="#38A169">{stats.resolved}</Typography>
                            <Typography variant="body2" color="text.secondary">Resolved</Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid size={{ xs: 6, md: 3 }}>
                    <Card sx={{ bgcolor: '#F7FAFC', border: '1px solid #E2E8F0' }}>
                        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                            <Typography variant="h3" fontWeight="bold" color="#4A5568">{stats.total}</Typography>
                            <Typography variant="body2" color="text.secondary">Total Tickets</Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Filters */}
            <Card sx={{ mb: 2 }}>
                <CardContent sx={{ py: 1.5, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                    <FilterListIcon color="action" />
                    <FormControl size="small" sx={{ minWidth: 150 }}>
                        <InputLabel>Status</InputLabel>
                        <Select value={filterStatus} label="Status" onChange={(e) => { setFilterStatus(e.target.value); setPage(0); }}>
                            <MenuItem value="all">All Status</MenuItem>
                            <MenuItem value="new">🔴 New</MenuItem>
                            <MenuItem value="in_progress">🟡 In Progress</MenuItem>
                            <MenuItem value="resolved">🟢 Resolved</MenuItem>
                            <MenuItem value="closed">⚫ Closed</MenuItem>
                        </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 170 }}>
                        <InputLabel>Category</InputLabel>
                        <Select value={filterCategory} label="Category" onChange={(e) => { setFilterCategory(e.target.value); setPage(0); }}>
                            <MenuItem value="all">All Categories</MenuItem>
                            {Object.entries(CATEGORY_LABELS).map(([key, val]) => (
                                <MenuItem key={key} value={key}>{val.icon} {val.label}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
                        Showing {filteredTickets.length} of {tickets.length} tickets
                    </Typography>
                </CardContent>
            </Card>

            {/* Tickets table */}
            {filteredTickets.length === 0 ? (
                <Card sx={{ p: 6, textAlign: 'center' }}>
                    <InboxIcon sx={{ fontSize: 48, color: '#ccc', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary">No tickets found</Typography>
                    <Typography variant="body2" color="text.secondary">
                        {filterStatus !== 'all' || filterCategory !== 'all'
                            ? 'Try adjusting your filters'
                            : 'When users submit the contact form, their messages will appear here'}
                    </Typography>
                </Card>
            ) : (
                <TableContainer component={Paper} sx={{ borderRadius: 3, overflowX: 'auto' }}>
                    <Table sx={{ minWidth: 900 }}>
                        <TableHead>
                            <TableRow sx={{ bgcolor: '#F8F9FA' }}>
                                <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>From</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Category</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Subject</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Priority</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Received</TableCell>
                                <TableCell sx={{ fontWeight: 700 }} align="center">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredTickets
                                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                .map((ticket) => {
                                    const cat = CATEGORY_LABELS[ticket.category] || CATEGORY_LABELS.other;
                                    const status = STATUS_CONFIG[ticket.status] || STATUS_CONFIG.new;
                                    const priority = PRIORITY_CONFIG[ticket.priority] || PRIORITY_CONFIG.normal;
                                    return (
                                        <TableRow
                                            key={ticket.id}
                                            hover
                                            sx={{
                                                cursor: 'pointer',
                                                bgcolor: isNewStatus(ticket.status) ? 'rgba(255,107,107,0.03)' : 'inherit',
                                                '&:hover': { bgcolor: 'rgba(255,107,107,0.06)' }
                                            }}
                                            onClick={() => handleViewTicket(ticket)}
                                        >
                                            <TableCell>
                                                <Chip label={status.label} color={status.color} size="small" />
                                            </TableCell>
                                            <TableCell>
                                                <Box>
                                                    <Typography variant="body2" fontWeight={600}>{ticket.name}</Typography>
                                                    <Typography variant="caption" color="text.secondary">{ticket.email}</Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={`${cat.icon} ${cat.label}`}
                                                    size="small"
                                                    sx={{ bgcolor: `${cat.color}15`, color: cat.color, fontWeight: 500 }}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" sx={{ maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                    {ticket.subject}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Chip label={priority.label} color={priority.color} size="small" variant="outlined" />
                                            </TableCell>
                                            <TableCell>
                                                <Tooltip title={formatDate(ticket.createdAt)}>
                                                    <Typography variant="body2" color="text.secondary">
                                                        {timeAgo(ticket.createdAt)}
                                                    </Typography>
                                                </Tooltip>
                                            </TableCell>
                                            <TableCell align="right" sx={{ whiteSpace: 'nowrap', width: 1, py: 1 }}>
                                                <Box sx={{ display: 'inline-flex', flexDirection: 'row', alignItems: 'center', gap: 0.5, flexWrap: 'nowrap' }}>
                                                <Tooltip title="View Details">
                                                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleViewTicket(ticket); }}>
                                                        <VisibilityIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Reply via Email">
                                                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); window.open(`mailto:${ticket.email}?subject=Re: ${ticket.subject}`); }}>
                                                        <ReplyIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Delete">
                                                    <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); setTicketToDelete(ticket.id); setDeleteConfirmOpen(true); }}>
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                        </TableBody>
                    </Table>
                    <TablePagination
                        rowsPerPageOptions={[10, 15, 25, 50]}
                        component="div"
                        count={filteredTickets.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={(_, p) => setPage(p)}
                        onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value)); setPage(0); }}
                    />
                </TableContainer>
            )}

            {/* Ticket Detail Dialog */}
            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
                {selectedTicket && (
                    <>
                        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Box>
                                <Typography variant="h6" fontWeight="bold">
                                    {(CATEGORY_LABELS[selectedTicket.category] || CATEGORY_LABELS.other).icon} {selectedTicket.subject}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    Ticket ID: {selectedTicket.id.slice(0, 8)}... • Received {formatDate(selectedTicket.createdAt)}
                                </Typography>
                            </Box>
                            <Chip
                                label={STATUS_CONFIG[selectedTicket.status]?.label || 'New'}
                                color={STATUS_CONFIG[selectedTicket.status]?.color || 'error'}
                            />
                        </DialogTitle>
                        <DialogContent dividers>
                            {/* Sender Info */}
                            <Box sx={{ display: 'flex', gap: 2, mb: 3, p: 2, bgcolor: '#F8F9FA', borderRadius: 2 }}>
                                <Avatar sx={{ bgcolor: '#FF6B9D', width: 48, height: 48 }}>
                                    {selectedTicket.name?.[0]?.toUpperCase() || '?'}
                                </Avatar>
                                <Box sx={{ flex: 1 }}>
                                    <Typography fontWeight={600}>{selectedTicket.name}</Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        <a href={`mailto:${selectedTicket.email}`} style={{ color: '#FF6B9D' }}>{selectedTicket.email}</a>
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 1, mt: .5 }}>
                                        <Chip
                                            label={`${(CATEGORY_LABELS[selectedTicket.category] || CATEGORY_LABELS.other).icon} ${(CATEGORY_LABELS[selectedTicket.category] || CATEGORY_LABELS.other).label}`}
                                            size="small"
                                        />
                                        <Chip label={`Source: ${selectedTicket.source || 'web'}`} size="small" variant="outlined" />
                                    </Box>
                                </Box>
                                <Button
                                    variant="contained"
                                    startIcon={<ReplyIcon />}
                                    size="small"
                                    sx={{ bgcolor: '#FF6B9D', '&:hover': { bgcolor: '#E55A8A' }, alignSelf: 'center' }}
                                    onClick={() => window.open(`mailto:${selectedTicket.email}?subject=Re: ${selectedTicket.subject}`)}
                                >
                                    Reply
                                </Button>
                            </Box>

                            {/* Message */}
                            <Typography variant="subtitle2" gutterBottom fontWeight={600}>Message</Typography>
                            <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: '#FAFAFA', borderRadius: 2 }}>
                                <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.7 }}>
                                    {selectedTicket.message}
                                </Typography>
                            </Paper>

                            <Divider sx={{ my: 2 }} />

                            {/* Status & Priority Controls */}
                            <Grid container spacing={2} sx={{ mb: 2 }}>
                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel>Status</InputLabel>
                                        <Select
                                            value={selectedTicket.status}
                                            label="Status"
                                            onChange={(e) => handleUpdateStatus(selectedTicket.id, e.target.value)}
                                        >
                                            <MenuItem value="new">🔴 New</MenuItem>
                                            <MenuItem value="in_progress">🟡 In Progress</MenuItem>
                                            <MenuItem value="resolved">🟢 Resolved</MenuItem>
                                            <MenuItem value="closed">⚫ Closed</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel>Priority</InputLabel>
                                        <Select
                                            value={selectedTicket.priority || 'normal'}
                                            label="Priority"
                                            onChange={(e) => handleUpdatePriority(selectedTicket.id, e.target.value)}
                                        >
                                            <MenuItem value="low">Low</MenuItem>
                                            <MenuItem value="normal">Normal</MenuItem>
                                            <MenuItem value="high">⚠️ High</MenuItem>
                                            <MenuItem value="urgent">🔴 Urgent</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>
                            </Grid>

                            {/* Admin Notes */}
                            <Typography variant="subtitle2" gutterBottom fontWeight={600}>Internal Notes</Typography>
                            <TextField
                                fullWidth
                                multiline
                                rows={3}
                                placeholder="Add internal notes about this ticket (not visible to user)..."
                                value={adminNotes}
                                onChange={(e) => setAdminNotes(e.target.value)}
                                variant="outlined"
                                size="small"
                            />
                            <Button
                                variant="outlined"
                                size="small"
                                sx={{ mt: 1, color: '#FF6B9D', borderColor: '#FF6B9D' }}
                                onClick={handleSaveNotes}
                            >
                                Save Notes
                            </Button>

                            {/* Metadata */}
                            {(selectedTicket.userAgent || selectedTicket.pageUrl) && (
                                <Box sx={{ mt: 2, p: 1.5, bgcolor: '#F8F9FA', borderRadius: 1 }}>
                                    <Typography variant="caption" color="text.secondary" display="block">
                                        <strong>Page URL:</strong> {selectedTicket.pageUrl || 'N/A'}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: .5 }}>
                                        <strong>User Agent:</strong> {selectedTicket.userAgent?.slice(0, 100) || 'N/A'}...
                                    </Typography>
                                </Box>
                            )}
                        </DialogContent>
                        <DialogActions>
                            <Button
                                color="error"
                                onClick={() => { setTicketToDelete(selectedTicket.id); setDeleteConfirmOpen(true); }}
                            >
                                Delete Ticket
                            </Button>
                            <Button onClick={() => setDialogOpen(false)}>Close</Button>
                        </DialogActions>
                    </>
                )}
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
                <DialogTitle>Delete Ticket?</DialogTitle>
                <DialogContent>
                    <Typography>Are you sure you want to permanently delete this support ticket? This action cannot be undone.</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
                    <Button color="error" variant="contained" onClick={handleDeleteTicket}>Delete</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default SupportInbox;
